import {
  Box,
  Button,
  Flex,
  HStack,
  NumberInput,
  Table,
  Text,
  type NumberInputValueChangeDetails,
} from '@chakra-ui/react';
import { Tooltip } from '@/components/ui/tooltip';
import { explainCopy } from '../../lib/explainCopy'
import { groupSeats } from './SeatBoard'
import type { SeatResult } from '../../types/electionSim'

const fmt = (n: number) => new Intl.NumberFormat('en-US').format(n)

export interface SeatAllocInput {
  seatCode: string
  marginal: number
  maxUsefulShifts: number
}

/**
 * Client-side mirror of the backend `GreedyAllocator.Suggest` (chunk = 100, cap at each seat's
 * max useful shifts). Pours `chunk` shifts at a time into the seat with the highest positive
 * marginal net-Dem-votes-per-100-shifts that isn't yet capped; ties resolve to the first seat in
 * input order (matching LINQ's stable `OrderByDescending`). Zero/negative-marginal seats never
 * receive shifts. Stops when the budget is exhausted or every seat is capped.
 */
export function greedyAllocate(
  totalShifts: number,
  seats: SeatAllocInput[],
  chunk = 100,
): Record<string, number> {
  const alloc: Record<string, number> = {}
  for (const s of seats) alloc[s.seatCode] = 0
  let remaining = totalShifts
  while (remaining > 0) {
    let best: SeatAllocInput | null = null
    for (const s of seats) {
      if (s.marginal > 0 && alloc[s.seatCode] < s.maxUsefulShifts) {
        if (best === null || s.marginal > best.marginal) best = s
      }
    }
    if (best === null) break
    const give = Math.min(chunk, remaining, best.maxUsefulShifts - alloc[best.seatCode])
    if (give <= 0) break
    alloc[best.seatCode] += give
    remaining -= give
  }
  return alloc
}

export interface AllocationTableProps {
  seats: SeatResult[]
  allocation: Record<string, number>
  totalShifts: number
  onChange: (seatCode: string, shifts: number) => void
  /** Greedy auto-suggest is deferred pending a backend per-seat capacity field (see report); the
   *  button is rendered disabled, so this handler is currently optional/unused. */
  onSuggest?: () => void
  onRun: () => void
  isRunning?: boolean
}

/**
 * Effort-allocation editor for the chamber scenario (spec §slice-4): target seats (must-hold +
 * reach) each get a shifts NumberInput, a win-probability Δ from the last run, and the whole board
 * carries a running total against the shift budget. Over-budget turns the footer red and disables
 * the run. "Suggest allocation" (greedy heuristic) is DEFERRED and rendered disabled — the pure
 * `greedyAllocate` is faithful to the backend but needs a per-seat capacity the backend doesn't yet
 * expose. "Print seat board" prints (this control chrome is `.no-print`, the seat board itself is
 * `.profile-card`). The running-total tooltip is wired to `explainCopy.allocation` (transparency
 * contract).
 */
export function AllocationTable({
  seats,
  allocation,
  totalShifts,
  onChange,
  onSuggest,
  onRun,
  isRunning,
}: AllocationTableProps) {
  const { mustHold, reach } = groupSeats(seats)
  const targets = [...mustHold, ...reach]
  const alloc = allocation ?? {}
  const used = targets.reduce((sum, s) => sum + (alloc[s.seatCode] ?? 0), 0)
  const overBudget = used > totalShifts

  return (
    <Box className="no-print" bg="surface" p={6} borderRadius="lg" shadow="md">
      <HStack gap={1} mb={1}>
        <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase">
          Effort allocation
        </Text>
      </HStack>
      <Text fontSize="sm" color="gray.500" mb={3}>
        Distribute your shift budget across the seats worth contesting.
      </Text>

      {targets.length === 0 ? (
        <Text fontSize="sm" color="gray.500">
          No Modeled must-hold or reach seats to allocate against in this run.
        </Text>
      ) : (
        <Box overflowX="auto">
        <Table.Root size="sm">
          <Table.Header bg="gray.50">
            <Table.Row>
              <Table.ColumnHeader>Seat</Table.ColumnHeader>
              <Table.ColumnHeader textAlign='end'>Shifts</Table.ColumnHeader>
              <Table.ColumnHeader textAlign='end'>Win prob Δ</Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {targets.map((s) => {
              const deltaPp = Math.round((s.winProbability - s.baselineWinProbability) * 100)
              return (
                <Table.Row key={s.seatCode}>
                  <Table.Cell>
                    <Text fontSize="sm" lineClamp={1} title={s.seatName}>
                      {s.seatName}
                    </Text>
                  </Table.Cell>
                  <Table.Cell textAlign='end'>
                    <NumberInput.Root
                      size="sm"
                      min={0}
                      value={String(alloc[s.seatCode] ?? 0)}
                      onValueChange={(details: NumberInputValueChangeDetails) =>
                        onChange(s.seatCode, Number.isNaN(details.valueAsNumber) ? 0 : details.valueAsNumber)
                      }
                      maxW="120px"
                      ml="auto"
                    >
                      <NumberInput.Input aria-label={`Shifts for ${s.seatName}`} />
                      <NumberInput.Control>
                        <NumberInput.IncrementTrigger />
                        <NumberInput.DecrementTrigger />
                      </NumberInput.Control>
                    </NumberInput.Root>
                  </Table.Cell>
                  <Table.Cell textAlign='end'>
                    {s.coverage === 'LeanOnly' ? (
                      <Text fontSize="xs" color="gray.500">
                        rated only
                      </Text>
                    ) : (
                      <Text
                        fontSize="sm"
                        fontWeight="semibold"
                        color={deltaPp > 0 ? 'green.600' : deltaPp < 0 ? 'red.500' : 'gray.500'}
                      >
                        {deltaPp > 0 ? `+${deltaPp} pp` : deltaPp < 0 ? `${deltaPp} pp` : '±0 pp'}
                      </Text>
                    )}
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
          <Table.Footer>
            <Table.Row>
              <Table.ColumnHeader>
                <HStack gap={1}>
                  <Text fontSize="xs" color="gray.500" textTransform="none" fontWeight="normal">
                    Running total
                  </Text>
                  <Tooltip showArrow content={explainCopy.allocation(totalShifts)} positioning={{
                    placement: "top"
                  }}>
                    <Box
                      as="span"
                      aria-label="Explain allocation"
                      tabIndex={0}
                      cursor="help"
                      fontSize="xs"
                      color="info.fg"
                      borderWidth="1px"
                      borderColor="info.fg"
                      borderRadius="full"
                      px={1.5}
                    >
                      i
                    </Box>
                  </Tooltip>
                </HStack>
              </Table.ColumnHeader>
              <Table.ColumnHeader textAlign='end'>
                <Text
                  fontSize="sm"
                  fontWeight="bold"
                  color={overBudget ? 'red.500' : 'gray.700'}
                >
                  {fmt(used)} / {fmt(totalShifts)}
                </Text>
              </Table.ColumnHeader>
              <Table.ColumnHeader />
            </Table.Row>
          </Table.Footer>
        </Table.Root>
        </Box>
      )}

      {overBudget && (
        <Text fontSize="xs" color="red.500" mt={2}>
          Allocation exceeds your shift budget — reduce shifts before running.
        </Text>
      )}

      <Flex mt={4} gap={3} wrap="wrap">
        {/* Auto-suggest is deferred: the greedy heuristic needs a per-seat capacity the backend
            doesn't yet expose (SeatResultDto has no max-useful-shifts). The affordance stays
            visible-but-disabled so it's discoverable when the backend fast-follow lands. */}
        <Tooltip
          showArrow
          content="Auto-suggest coming soon — needs per-seat capacity from the backend"
          positioning={{
            placement: "top"
          }}
        >
          {/* Chakra skips events (incl. tooltip) on a disabled button, so wrap it in a focusable
              span to keep the tooltip reachable. */}
          <Box as="span" tabIndex={0}>
            <Button size="sm" variant="outline" disabled onClick={onSuggest}>
              Suggest allocation (greedy heuristic)
            </Button>
          </Box>
        </Tooltip>
        <Button size="sm" variant="outline" onClick={() => window.print()}>
          Print seat board
        </Button>
        <Button
          size="sm"
          colorPalette="brand"
          onClick={onRun}
          disabled={overBudget}
          loading={isRunning}
        >
          Run scenario with allocation
        </Button>
      </Flex>
    </Box>
  );
}
